import {
  IErrorCenterListResponse,
  IErrorOccurrence,
  IErrorTriageDetail,
  IErrorTriageListItem,
} from '@common/interfaces/errorTriage'
import { combineReducers } from 'redux'
import { createLoadingDataReducer } from '../../helpers/redux'
import { IAction, IActionWithParams } from '../apiMiddleware'
import {
  AUTO_FIX_ERROR_TRIAGE_SUCCESS,
  INVESTIGATE_ERROR_TRIAGE_SUCCESS,
  LIST_ERROR_OCCURRENCES_REQUEST,
  LIST_ERROR_TRIAGE_OCCURRENCES_REQUEST,
  LIST_ERROR_TRIAGE_REQUEST,
  RESET_ERROR_TRIAGE_DETAIL,
  SHOW_ERROR_TRIAGE_FAILURE,
  SHOW_ERROR_TRIAGE_REQUEST,
  SHOW_ERROR_TRIAGE_SUCCESS,
  UPDATE_ERROR_TRIAGE_STATE_SUCCESS,
  prefix,
} from './actions'
import {
  IErrorOccurrenceListQuery,
  IErrorTriageListQuery,
  IErrorTriageListState,
  IListOptions,
  IListQuery,
  IPage,
  IPagedListState,
  replaceErrorTriageItem,
} from './helpers'

/** Mutations answer with the full detail, which also refreshes the row in the list. */
const MUTATION_SUCCESS_ACTIONS = [
  UPDATE_ERROR_TRIAGE_STATE_SUCCESS,
  AUTO_FIX_ERROR_TRIAGE_SUCCESS,
  INVESTIGATE_ERROR_TRIAGE_SUCCESS,
]

type IListAction = IActionWithParams<IListOptions & Record<string, unknown>>

/** A `{ data, total }` list on the shared loading reducer; pages for a query it has left are dropped. */
const createPagedListReducer = <T, Q extends IListQuery>(
  loadingTypes: string[],
  pickQuery: (params: Record<string, unknown>) => Q,
  resetTypes: string[] = [],
) =>
  createLoadingDataReducer<IPage<T> | null>(
    loadingTypes,
    undefined,
    false,
    resetTypes,
    false,
    undefined,
    undefined,
    { pickQuery },
  ) as unknown as (
    state: IPagedListState<T, Q> | undefined,
    action: IListAction,
  ) => IPagedListState<T, Q>

const pickListQuery = ({ filters, orderBy, orderDirection }: Record<string, unknown>) => ({
  filters: (filters ?? {}) as Record<string, unknown>,
  orderBy: orderBy as string,
  orderDirection: orderDirection as string,
})

const pickTriageQuery = (params: Record<string, unknown>): IErrorTriageListQuery => ({
  ...pickListQuery(params),
  state: params.state as IErrorTriageListQuery['state'],
})

const pickOccurrenceQuery = (params: Record<string, unknown>): IErrorOccurrenceListQuery => ({
  ...pickListQuery(params),
  ...(typeof params.errorTriageId === 'string' && { errorTriageId: params.errorTriageId }),
})

const baseList = createPagedListReducer<IErrorTriageListItem, IErrorTriageListQuery>(
  [LIST_ERROR_TRIAGE_REQUEST],
  pickTriageQuery,
)

export function list(state: IErrorTriageListState | undefined, action: IListAction) {
  const next = baseList(state, action)
  if (MUTATION_SUCCESS_ACTIONS.includes(action.type) && action.data) {
    return replaceErrorTriageItem(next, action.data as IErrorTriageListItem)
  }
  return next
}

/**
 * Null until any Error Center answer reports it — the grouped list, the
 * occurrence lists and the detail all carry it, so a deep link learns it too.
 * Unknown is treated as off, see getTriageBadge.
 */
export function isTriageEnabled(state: boolean | null = null, action: IAction): boolean | null {
  if (!action.type.startsWith(`${prefix}/`)) return state
  const reported = (action.data as Partial<IErrorCenterListResponse> | undefined)?.isTriageEnabled
  return typeof reported === 'boolean' ? reported : state
}

export const liveOccurrences = createPagedListReducer<IErrorOccurrence, IErrorOccurrenceListQuery>(
  [LIST_ERROR_OCCURRENCES_REQUEST],
  pickOccurrenceQuery,
)

export const detailOccurrences = createPagedListReducer<
  IErrorOccurrence,
  IErrorOccurrenceListQuery
>([LIST_ERROR_TRIAGE_OCCURRENCES_REQUEST], pickOccurrenceQuery, [RESET_ERROR_TRIAGE_DETAIL])

export interface IErrorTriageDetailState {
  id: string | null
  data: IErrorTriageDetail | null
  isLoading: boolean
}

const INITIAL_DETAIL: IErrorTriageDetailState = { id: null, data: null, isLoading: false }

type IDetailAction = IActionWithParams<{ id?: string; skipLoader?: boolean }>

export function detail(
  state: IErrorTriageDetailState = INITIAL_DETAIL,
  action: IDetailAction,
): IErrorTriageDetailState {
  const actionId = action.params?.id ?? null
  switch (action.type) {
    case SHOW_ERROR_TRIAGE_REQUEST:
      if (actionId === state.id) {
        return { ...state, isLoading: state.isLoading || !action.params?.skipLoader }
      }
      return { id: actionId, data: null, isLoading: true }
    case SHOW_ERROR_TRIAGE_SUCCESS:
      return actionId === state.id
        ? { ...state, data: action.data as IErrorTriageDetail, isLoading: false }
        : state
    case SHOW_ERROR_TRIAGE_FAILURE:
      return actionId === state.id ? { ...state, isLoading: false } : state
    case RESET_ERROR_TRIAGE_DETAIL:
      return INITIAL_DETAIL
    default:
      if (MUTATION_SUCCESS_ACTIONS.includes(action.type) && actionId === state.id && action.data) {
        return { ...state, data: action.data as IErrorTriageDetail }
      }
      return state
  }
}

export default combineReducers({
  list,
  isTriageEnabled,
  liveOccurrences,
  detailOccurrences,
  detail,
})
